import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiSave, FiArrowLeft, FiDollarSign } from 'react-icons/fi';
import siteVisitService from '../services/siteVisitService';
import Input from '../components/Input';
import Button from '../components/Button';
import './TechnicianForm.css';

const UpdatePayment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [visit, setVisit] = useState(null);
  const [formData, setFormData] = useState({
    totalAmount: '',
    amountPaid: '',
    paymentStatus: 'pending',
    paymentMethod: '',
    paymentNotes: '',
  });

  useEffect(() => {
    fetchVisit();
  }, [id]);

  const fetchVisit = async () => {
    try {
      const response = await siteVisitService.getById(id);
      const visitData = response.data;
      setVisit(visitData);

      if (visitData.totalCost) {
        setFormData({
          totalAmount: visitData.totalCost.totalAmount || '',
          paymentStatus: visitData.paymentStatus || 'pending',
          amountPaid: visitData.amountPaid || '',
          paymentMethod: visitData.paymentMethod || '',
          paymentNotes: visitData.paymentNotes || '',
        });
      }
    } catch (error) {
      toast.error('Failed to fetch site visit details');
      navigate('/site-visits');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const calculateBalance = () => {
    const total = parseFloat(formData.totalAmount) || 0;
    const paid = parseFloat(formData.amountPaid) || 0;
    return total - paid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const totalAmount = parseFloat(formData.totalAmount) || 0;
    const amountPaid = parseFloat(formData.amountPaid) || 0;

    // Auto-determine payment status based on amount paid
    let paymentStatus = formData.paymentStatus;
    if (amountPaid === 0) {
      paymentStatus = 'pending';
    } else if (amountPaid >= totalAmount) {
      paymentStatus = 'paid';
    } else {
      paymentStatus = 'partial';
    }

    const submitData = {
      totalCost: {
        totalAmount,
      },
      paymentStatus: paymentStatus,
      amountPaid: amountPaid,
      paymentMethod: formData.paymentMethod || undefined,
      paymentNotes: formData.paymentNotes || undefined,
    };

    try {
      await siteVisitService.updatePayment(id, submitData);
      toast.success('Payment details updated successfully');
      navigate(`/site-visits/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update payment');
    } finally {
      setLoading(false);
    }
  };

  if (!visit) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  const totalAmount = parseFloat(formData.totalAmount) || 0;
  const balance = calculateBalance();

  return (
    <div className="form-container">
      <div className="form-header">
        <button onClick={() => navigate(`/site-visits/${id}`)} className="back-button">
          <FiArrowLeft /> Back
        </button>
        <h2 className="form-title">Update Payment Details</h2>
        <p style={{ color: 'var(--gray-600)', marginTop: '0.5rem' }}>
          Ticket: #{visit.ticketNumber} - {visit.customer.name}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="form-card">
        <div className="form-section">
          <h3 className="section-title">Cost Details</h3>
          <div style={{ 
            padding: '1.5rem', 
            background: 'var(--primary-50)', 
            borderRadius: 'var(--radius-lg)',
            marginTop: '0.5rem',
            textAlign: 'center'
          }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--gray-600)', fontWeight: '600' }}>
              TOTAL AMOUNT (from completed visit)
            </span>
            <div style={{ 
              fontSize: '2.5rem', 
              fontWeight: '800', 
              color: 'var(--primary-700)',
              fontFamily: 'var(--font-display)'
            }}>
              ₹{totalAmount.toLocaleString()}
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Payment Information</h3>
          <div className="form-grid">
            <Input
              label="Amount Paid (₹)"
              type="number"
              name="amountPaid"
              value={formData.amountPaid}
              onChange={handleChange}
              placeholder="2300"
              min="0"
              step="0.01"
            />

            <div className="input-group">
              <label className="input-label">Payment Method</label>
              <select
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleChange}
                className="input-field"
              >
                <option value="">Select Method</option>
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="card">Card</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cheque">Cheque</option>
              </select>
            </div>
          </div>

          {balance !== 0 && (
            <div style={{ 
              padding: '1.5rem', 
              background: balance > 0 ? 'var(--warning-50)' : 'var(--success-50)', 
              borderRadius: 'var(--radius-lg)',
              marginTop: '1rem',
              textAlign: 'center'
            }}>
              <span style={{ fontSize: '0.875rem', color: 'var(--gray-600)', fontWeight: '600' }}>
                {balance > 0 ? 'BALANCE DUE' : 'OVERPAID'}
              </span>
              <div style={{ 
                fontSize: '2rem', 
                fontWeight: '800', 
                color: balance > 0 ? 'var(--warning-700)' : 'var(--success-700)',
                fontFamily: 'var(--font-display)'
              }}>
                ₹{Math.abs(balance).toLocaleString()}
              </div>
            </div>
          )}

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '1rem',
            marginTop: '1rem',
            padding: '1rem',
            background: 'var(--gray-50)',
            borderRadius: 'var(--radius-lg)'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)', marginBottom: '0.25rem' }}>
                TOTAL
              </div>
              <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--gray-900)' }}>
                ₹{totalAmount.toLocaleString()}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)', marginBottom: '0.25rem' }}>
                PAID
              </div>
              <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--success-600)' }}>
                ₹{(parseFloat(formData.amountPaid) || 0).toLocaleString()}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)', marginBottom: '0.25rem' }}>
                BALANCE
              </div>
              <div style={{ fontSize: '1.25rem', fontWeight: '700', color: balance > 0 ? 'var(--error-600)' : 'var(--success-600)' }}>
                ₹{Math.abs(balance).toLocaleString()}
              </div>
            </div>
          </div>

          <div className="input-group" style={{ marginTop: '1rem' }}>
            <label className="input-label">Payment Notes</label>
            <textarea
              name="paymentNotes"
              value={formData.paymentNotes}
              onChange={handleChange}
              placeholder="Any notes about payment, installments, etc."
              className="input-field"
              rows="3"
            />
          </div>
        </div>

        <div className="form-actions">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(`/site-visits/${id}`)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            icon={<FiDollarSign />}
            loading={loading}
          >
            Update Payment
          </Button>
        </div>
      </form>
    </div>
  );
};

export default UpdatePayment;
